import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import BackButton from "components/common/BackButton";
import { DocumentationViewer } from "./DocumentationViewer";
import { PageLoader } from "components/common/feedback/PageLoader";
import { PageError } from "components/common/feedback/PageError";
import { bugsnagClient } from "../../bugsnag";
import type { NotifiableError } from "@bugsnag/core/types/common";
import { useAuth } from "../../contexts/AuthContext";
import { ANALYTICS_EVENTS, trackEvent } from "../../utils/analytics";
import api from "../../utils/api";
import { convertSvgToPng } from "utils/graphics/convertSvgToPng";
import { useQuery } from "hooks/useQuery";
import { useRequest } from "hooks/useRequest";
import { DocumentationProps, Documentation } from "./types";

const DocumentationViewerWrapper = ({
  isViewMode = false,
}: DocumentationProps) => {
  const { t } = useTranslation();

  const { user } = useAuth();

  const query = useQuery();
  const { id: projectId, documentationId } = useParams();

  const targetFrame = query.get("frame");
  const targetObject = query.get("object");

  const [documentation, setDocumentation] = useState<Documentation | null>(
    null,
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchDocumentation = useCallback(async () => {
    try {
      const result = await api.getDocumentation(documentationId);
      setDocumentation(result);
    } catch (error) {
      bugsnagClient.notify(error as NotifiableError);
      setError("Failed to load documentation data.");
    }
  }, [documentationId]);

  const { isLoading: isUploadingPreviewImage, run: uploadPreviewImage } =
    useRequest(
      async (svgElement: SVGSVGElement) => {
        const svgFile = await convertSvgToPng({ svgElement });

        const response = await api.uploadDocumentationPreview(
          documentationId,
          svgFile,
        );

        return response;
      },
      { manual: true },
    );

  useEffect(() => {
    const fetchDocumentation = async () => {
      try {
        const result = await api.getDocumentation(documentationId);
        setDocumentation(result);
        trackEvent(ANALYTICS_EVENTS.DOCUMENTATION_VIEWED, {
          documentationId,
          projectId,
        });
      } catch (error) {
        bugsnagClient.notify(error as NotifiableError);
        setError("Failed to load documentation data.");
      } finally {
        setLoading(false);
      }
    };
    fetchDocumentation();
  }, [documentationId]);

  if (loading || !projectId || !documentation) {
    return <PageLoader message={t("documentation.loading")} />;
  }

  if (error) {
    return (
      <PageError
        message={t("documentation.error_loading")}
        messageDetail={t("documentation.error_retry")}
      />
    );
  }

  if (documentation?.status !== "done" && !user?.isGlobalAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600">{t("documentation.not_done")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isViewMode && (
        <div className="p-4 border-b">
          <BackButton
            label={documentation?.sectionId?.name ?? ""}
            to={`/project/${documentation?.projectId._id}/sections/${documentation?.sectionId._id}/documentation`}
          />
        </div>
      )}
      <DocumentationViewer
        targetFrame={targetFrame}
        targetObject={targetObject}
        projectId={projectId}
        documentation={documentation}
        refetchDocumentation={refetchDocumentation}
        uploadPreviewImage={uploadPreviewImage}
        isUploadingPreviewImage={isUploadingPreviewImage}
        isViewMode={isViewMode}
      />
    </>
  );
};

export default DocumentationViewerWrapper;
